import { useState } from "react";
import { Check, Copy } from "lucide-react";
import clsx from "clsx";
import { useT } from "../i18n";
import { buttonClass, type ButtonVariant } from "./Button";

/** Small button that copies `value` to the clipboard and shows a check mark for a moment. */
export function CopyButton({ value, label, className, size = "sm", variant = "secondary" }: { value: string; label?: string; className?: string; size?: "sm" | "md"; variant?: ButtonVariant }) {
  const t = useT();
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  }
  return (
    <button type="button" onClick={copy} className={buttonClass(variant, size, className)} aria-live="polite">
      {done ? <Check className="h-3.5 w-3.5 text-ok" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
      {done ? t("ui.copied") : (label ?? t("ui.copy"))}
    </button>
  );
}

/** Inline monospace value with a copy button – for tokens, DNS records, temporary passwords. */
export function CopyField({ value, className, large }: { value: string; className?: string; large?: boolean }) {
  return (
    <div className={clsx("flex items-center gap-2 rounded-control border border-line bg-surface-2 px-2.5 py-1.5", className)}>
      <code className={clsx("min-w-0 flex-1 break-all font-mono", large ? "text-lg" : "text-[12.5px]")}>{value}</code>
      <CopyButton value={value} />
    </div>
  );
}
